// src/components/common/MobileSheet.jsx
import * as React from 'react';
import { Box, Dialog, SwipeableDrawer, useMediaQuery, useTheme } from '@mui/material';
import { BOTTOM_SHEET_TRANSITION } from '../../theme/tokens';

/**
 * MobileSheet
 * On mobile (< sm): children appear in a bottom-anchored SwipeableDrawer
 *   with a drag handle, rounded top corners, and backdrop blur.
 *   DialogTitle/DialogContent/DialogActions padding is reduced to fit smaller screens.
 * On desktop: children appear in a standard Dialog.
 *
 * Drop-in replacement — keep DialogTitle / DialogContent / DialogActions as children.
 */
export default function MobileSheet({
  open,
  onClose,
  children,
  maxWidth = 'sm',
  fullWidth = true,
  // 🔴 Optional (R16-W1, 2026-11-04): Kennung des Elements, das den Dialog
  // benennt. Ohne sie bleibt die Flaeche `role="presentation"`; Fokusfalle und
  // Escape kommen von `Modal` und funktionieren, eine Ansage „Dialog, <Name>"
  // gibt es aber nicht (WCAG 4.1.2). Aufrufer ohne `titelId` bleiben
  // unveraendert.
  titelId,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="bottom"
        open={open}
        onOpen={() => {}}
        onClose={onClose}
        disableSwipeToOpen
        PaperProps={{
          ...(titelId
            ? { role: 'dialog', 'aria-modal': 'true', 'aria-labelledby': titelId }
            : {}),
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            bgcolor: 'background.paper',
            backdropFilter: 'blur(24px) saturate(180%)',
            WebkitBackdropFilter: 'blur(24px) saturate(180%)',
            maxHeight: '88vh',
            display: 'flex',
            flexDirection: 'column',
            // Tighten MUI Dialog sub-component padding for bottom sheets
            '& .MuiDialogTitle-root': { pt: 1.5, pb: 1, px: 2.5 },
            '& .MuiDialogContent-root': { px: 2.5, py: 1.5 },
            '& .MuiDialogActions-root': { px: 2.5, pb: `calc(env(safe-area-inset-bottom) + 12px)` },
          },
        }}
        {...BOTTOM_SHEET_TRANSITION}
      >
        {/* iOS-style drag handle */}
        <Box sx={{
          width: 44, height: 4, borderRadius: 2,
          bgcolor: 'divider', mx: 'auto',
          mt: 1.5, mb: 0.5, flexShrink: 0,
        }} />
        {children}
      </SwipeableDrawer>
    );
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      aria-labelledby={titelId || undefined}
    >
      {children}
    </Dialog>
  );
}
